import { useState } from 'react';
import { DocumentationSection } from '@/hooks/useDocumentation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ReactMarkdown from 'react-markdown';
import { Save, Eye } from 'lucide-react';

interface ManualEditorProps {
  section: DocumentationSection | null;
  onSave: (sectionId: string, content: string) => void;
  isSaving: boolean;
}

export function ManualEditor({ section, onSave, isSaving }: ManualEditorProps) {
  const [editedContent, setEditedContent] = useState(section?.content || '');

  if (!section) {
    return (
      <Card className="p-6 h-full flex items-center justify-center">
        <p className="text-muted-foreground">Selecteer een sectie om te bewerken</p>
      </Card>
    );
  }

  const handleSave = () => {
    onSave(section.id, editedContent);
  };

  return (
    <Card className="p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold">{section.title}</h3>
        <Button onClick={handleSave} disabled={isSaving} size="sm">
          <Save className="h-4 w-4 mr-2" />
          Opslaan
        </Button>
      </div>

      <Tabs defaultValue="edit" className="flex-1 flex flex-col">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="edit">Bewerken</TabsTrigger>
          <TabsTrigger value="preview">
            <Eye className="h-4 w-4 mr-2" />
            Preview
          </TabsTrigger>
        </TabsList>
        <TabsContent value="edit" className="flex-1 mt-4">
          <Textarea
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            className="h-full min-h-[400px] font-mono text-sm"
            placeholder="Schrijf hier de handleiding content in Markdown formaat..."
          />
        </TabsContent>
        <TabsContent value="preview" className="flex-1 mt-4 overflow-y-auto">
          <div className="prose prose-sm max-w-none dark:prose-invert">
            <ReactMarkdown>{editedContent}</ReactMarkdown>
          </div>
        </TabsContent>
      </Tabs>
    </Card>
  );
}
